/**
 * useReplayRecorder Hook for Alien 2048
 *
 * Manages replay recording using the shared ReplayRecorder service.
 * Includes client-side buffering and handles network interruptions.
 *
 * @param gamePlayUUID - Unique ID for this gameplay session
 * @param userId - User ID from new auth system (if logged in)
 */

import { useRef, useEffect, useCallback } from 'react';
import ReplayRecorder from '../../../services/alien2048ReplayRecorder';
import logger from '../../../utils/logger';
import { LEVEL_ID } from '../constants';
import { Tile } from '../types';

// Grid type is an array of rows, each containing Tile or null
type Grid = (Tile | null)[][];

// Result type for finalize operation
interface FinalizeResult {
  success: boolean;
  score_id: string | null;
  validated_score: number | null;
  validation_errors: string[] | null;
}

const LEVEL_NAME = 'Alien 2048';
const GAME_STATE_NOT_YET_BEGUN = 0;

export const useReplayRecorder = (
  gamePlayUUID: string,
  userId: string | null = null
) => {
  const replayRecorderRef = useRef<ReplayRecorder | null>(null);

  /**
   * Create a replay session
   * @param playerName - Player name (optional)
   * @param initialGrid - Initial game grid state
   * @returns Promise<boolean> Success status
   */
  const createReplaySession = useCallback(async (
    playerName: string | null,
    initialGrid: Grid
  ): Promise<boolean> => {
    if (replayRecorderRef.current) {
      logger.debug('Replay recorder already exists', null, 'replay');
      return false;
    }

    // Session data for server
    const sessionData: any = {
      id: gamePlayUUID,
      level_id: LEVEL_ID,
      level_name: LEVEL_NAME,
      game_state: GAME_STATE_NOT_YET_BEGUN,
    };

    // Only add player_name if it exists
    if (playerName) {
      sessionData.player_name = playerName;
    }

    // Add user_id from new auth system
    if (userId) {
      sessionData.user_id = userId;
    }

    // Create replay recorder instance
    replayRecorderRef.current = new ReplayRecorder(gamePlayUUID, sessionData);

    // Record initial board state (buffered in memory)
    const initialBoardChanges: Array<[number, number, number]> = [];
    for (let row = 0; row < initialGrid.length; row++) {
      for (let col = 0; col < initialGrid[row].length; col++) {
        const cell = initialGrid[row][col];
        if (cell !== null) {
          initialBoardChanges.push([row, col, cell.value]);
        } else {
          initialBoardChanges.push([row, col, 0]);
        }
      }
    }

    if (initialBoardChanges.length > 0) {
      logger.debug('Recording initial board state', { changes: initialBoardChanges.length }, 'replay');
      replayRecorderRef.current.recordBoardInit(initialBoardChanges, initialGrid.length);
    }

    // Create session on server immediately
    // If this fails, recording will continue in memory but won't be saved to server
    try {
      await replayRecorderRef.current.createSession();
      // Start periodic flush timer after session is created
      replayRecorderRef.current.startPeriodicFlush();
      logger.debug('Replay session created on server', { sessionId: gamePlayUUID }, 'replay');
      return true;
    } catch (error) {
      logger.error('Failed to create replay session on server, recording disabled', { error: (error as Error).message }, 'replay');
      // Clean up recorder since session creation failed
      replayRecorderRef.current = null;
      return false;
    }
  }, [gamePlayUUID, userId]);

  /**
   * Record a move event
   * @param elapsedTime - Elapsed time in ms
   * @param direction - Direction of move ('up', 'down', 'left', 'right')
   * @param validMove - Whether the move was valid
   */
  const recordMove = useCallback((
    elapsedTime: number,
    direction: string,
    validMove: boolean
  ) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.recordEvent('MOVE', elapsedTime, {
        direction,
        valid: validMove
      });
    }
  }, []);

  /**
   * Record board state changes (tile movements, merges, new tiles)
   * @param elapsedTime - Elapsed time in ms
   * @param stateChanges - Array of [row, col, newValue]
   * @param changeType - Type of change ('move', 'merge', 'new_tile')
   */
  const recordBoardState = useCallback((
    elapsedTime: number,
    stateChanges: Array<[number, number, number]>,
    changeType: string = 'move'
  ) => {
    if (replayRecorderRef.current && stateChanges.length > 0) {
      replayRecorderRef.current.recordBoardState(elapsedTime, stateChanges, changeType);
    }
  }, []);

  /**
   * Record a score update
   * @param elapsedTime - Elapsed time in ms
   * @param newScore - New total score
   * @param points - Points added
   * @param reason - Reason for score update ('merge', 'tile_created')
   */
  const recordScoreUpdate = useCallback((
    elapsedTime: number,
    newScore: number,
    points: number,
    reason: string
  ) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.recordScoreUpdate(elapsedTime, newScore, points, reason);
    }
  }, []);

  /**
   * Record game state change
   * @param elapsedTime - Elapsed time in ms
   * @param gameState - New game state
   * @param movesUsed - Current moves used
   * @param duration - Duration in ms
   */
  const recordGameState = useCallback((
    elapsedTime: number,
    gameState: number,
    movesUsed: number,
    duration: number
  ) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.recordGameState(elapsedTime, gameState, movesUsed, duration);
    }
  }, []);

  /**
   * Update the session (e.g., mark as in progress)
   * @param updates - Updates to apply to session
   */
  const updateSession = useCallback((updates: any) => {
    if (replayRecorderRef.current) {
      replayRecorderRef.current.updateSession(updates);
    }
  }, []);

  /**
   * Finalize the replay
   * @param finalData - Final game data
   * @param finalData.final_score - Final score
   * @param finalData.final_moves - Final moves
   * @param finalData.game_state - Final game state
   * @param finalData.duration_ms - Duration in ms
   * @param finalData.final_board - Final board state (Grid)
   * @returns Promise<FinalizeResult> - Server response with score_id, validated_score, validation_errors
   */
  const finalizeReplay = useCallback(async (finalData: {
    final_score: number;
    final_moves: number;
    game_state: number;
    duration_ms: number;
    final_board?: Grid;
    highest_tile?: number;
  }): Promise<FinalizeResult> => {
    if (replayRecorderRef.current) {
      return await replayRecorderRef.current.finalize(finalData) as FinalizeResult;
    }
    return { success: false, score_id: null, validated_score: null, validation_errors: ['No replay recorder'] };
  }, []);

  /**
   * Check if replay recorder is initialized
   * @returns boolean
   */
  const isRecorderReady = useCallback(() => {
    return !!replayRecorderRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Note: We don't finalize here as that's handled by the component
      // This is just cleanup
      if (replayRecorderRef.current) {
        logger.debug('Cleaning up replay recorder', null, 'replay');
      }
    };
  }, []);

  return {
    // Session management
    createReplaySession,
    updateSession,
    finalizeReplay,
    isRecorderReady,

    // Event recording
    recordMove,
    recordBoardState,
    recordScoreUpdate,
    recordGameState,

    // Direct ref access (use sparingly)
    replayRecorderRef,
  };
};

export default useReplayRecorder;
